import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTextLanguage } from '../../../hooks/useShared';
import styled from 'styled-components';
import { Tooltip, Box } from '@mui/material';

const Step = styled.span`
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    color: '#3d0f13',
  }
`

const AdditionalErrors = styled.span`
  font-size: 10px;
`

/**
 * ErrorMessages
 * Props:
 * @param {Array<Object>} errorLists         - List of errors
 * @param {String}        description        - Translated description
 * @param {Function}      onClick            - Funciton to move form to a particular step
 * @param {Number}        activeGroupIndex   - Index for the active group
 */
const ErrorMessages = ({ errorsList = [], description, onClick, activeGroupIndex }) => {
  const stepsWithErrors = new Set(errorsList.map((elem) => elem.step));
  const { t } = useTranslation()

  // Tracks if there are errors to show at all
  const [isVisible, setIsVisible] = useState(false);
  // Tracks whether the alert is expanded (full list) or minimized (circle)
  const [isMinimized, setIsMinimized] = useState(false);

  // ? Utility state for fixing visual bug
  const [isMinimizedTextShown, setIsMinimizedTextShown] = useState(true)

  useEffect(() => {
    if (errorsList && Object.keys(errorsList).length > 0) {
      setIsVisible(true);
      setIsMinimized(false);
      setIsMinimizedTextShown(true)
    } else {
      setIsVisible(false);
    }
  }, [errorsList]);

  const items = useMemo(() => {
    if (!stepsWithErrors || stepsWithErrors.size === 0) return [];
    const result = [];
    for (const step of Array.from(stepsWithErrors)) {
      const found = errorsList.find((e) => Number(e.step) === Number(step));
      if (found) result.push({ step, field: found });
    }
    return result;
  }, [stepsWithErrors, errorsList]);

  if (!isVisible || items.length === 0) return null;

  const maximize = () => {
    setIsMinimizedTextShown(true)
    setTimeout(() => {
      setIsMinimized(false)
    }, 200);
  }

  return (
    <AnimatePresence>
      <motion.div
        // The 'layout' prop automatically animates size and border-radius changes
        layout
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.8 }}
        onClick={() => isMinimized && maximize()}
        style={{
          // Bootstrap 'danger' alert colors
          backgroundColor: '#f8d7da',
          color: '#842029',
          border: '1px solid #f5c2c7',
          // Morphing styles based on state
          borderRadius: isMinimized ? '50px' : '8px',
          width: isMinimized ? '30px' : '100%',
          maxWidth: isMinimized ? '30px' : '100%',
          height: isMinimized ? '30px' : 'auto',
          marginBlock: '20px',
          padding: isMinimized ? '0' : '1rem 1rem',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: isMinimized ? 'center' : 'flex-start',
          alignItems: isMinimized ? 'center' : 'stretch',
          overflow: isMinimized ? 'visible' : 'hidden',
          cursor: isMinimized ? 'pointer' : 'default',
          boxShadow: isMinimized ? '0 4px 12px rgba(220, 53, 69, 0.4)' : 'none',
          position: 'relative'
        }}
      >
        {/* AnimatePresence swaps the content smoothly */}
        <AnimatePresence mode="wait">
          {isMinimized ? (
            <div
              style={{
                position: 'relative',
                display:'flex',
              }}
            >
              <motion.div
                key="minimized-icon"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0 }}
              >
                {/* Warning Icon when minimized */}
                <span style={{ fontSize: '18px', fontWeight: 'bold', lineHeight: 1 }}>!</span>
              </motion.div>

              {/* Notice text rendered outside the small circle so it doesn't get clipped */}
              { isMinimized && (
                <motion.div
                  style={{
                    position: 'absolute',
                    left: '30px',
                    color: 'rgb(255, 113, 113)',
                    backgroundColor: 'rgba(255, 113, 113, .12)',
                    borderLeft: '3px solid rgb(255, 113, 113)',
                    borderBottomRightRadius: '6px',
                    borderTopRightRadius: '6px',
                    padding: '2px 8px',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {t('errorComponent.notice')}
                </motion.div>
              )}
            </div>
          ) : (
            <motion.div
              key="expanded-content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{ width: '100%' }}
            > 
              { isMinimizedTextShown && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <strong style={{ fontSize: '0.9rem' }}>
                      {t('errorComponent.title')}
                    </strong>
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent triggering the parent onClick
                        setIsMinimizedTextShown(false)
                        setTimeout(() => {
                          setIsMinimized(true);
                        }, 200);
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#842029',
                        fontSize: '1.2rem',
                        cursor: 'pointer',
                        padding: '0 5px',
                        lineHeight: 1,
                      }}
                      aria-label="Minimizar"
                    >
                      ✕
                    </button>
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.6', fontSize: '0.8rem' }}>
                    {items
                      .sort((a, b) => a?.step - b?.step)
                      .map(({ step, field }, index) => {
                        const otherErrors = (errorsList.filter(fieldWithError => fieldWithError?.step == step )?.length) - 1 
                        const listOtherErrors = errorsList.filter(fieldWithError => fieldWithError?.step == step )
                        listOtherErrors.shift()
                        const listOtherErrorsMarkup = (
                          <Box component="ul" sx={{ m: 0, pl: 2, fontSize: '0.85rem' }}>
                            {
                              listOtherErrors.map(element => {
                                return (
                                  <li>{element?.[description]}</li>
                                )
                              })
                            }
                          </Box>
                        )

                        return (<li key={`${step}-${field.id}`}>
                          <Step 
                            style={{ 
                              fontWeight: '600' 
                              
                            }}
                            onClick={() => onClick(step - 1)}
                          >
                            { t('stepper.step') } { step }
                          </Step>
                          : &nbsp;
                          {field?.[description]}
                          {
                            otherErrors > 0 && (
                              <Tooltip
                                placement='right'
                                arrow
                                title={listOtherErrorsMarkup}
                              >
                                <AdditionalErrors
                                >
                                &nbsp;
                                {t('errorComponent.and')} {otherErrors} {t('errorComponent.more')}
                                </AdditionalErrors>
                              </Tooltip>
                            )
                          }
                          
                        </li>
                      )})}
                  </ul>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};

export default ErrorMessages;